import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { AppState } from "../../../store/reducers";
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  Subject,
} from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import {
  NbButtonModule,
  NbDialogService,
  NbToastrService,
  NbTooltipModule,
} from "@nebular/theme";
import { Permissions } from "../../../enums/permissions.enum";
import {
  AssetTreeAdjustments,
  AssetViewModel,
  HelixAssetTreeClient,
} from "@helix/client";
import { TreeDragDropService, TreeNode } from "primeng/api";
import { Tree } from "primeng/tree";
import {
  faCompressArrowsAlt,
  faEdit,
  faExpandArrowsAlt,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { DeleteConfirmComponent } from "../delete-confirm/delete-confirm.component";
import { GetAssetTree } from "../../../store/actions/dashboard.actions";

import { selectSelectedAssetId } from "../../../store/selectors/dashboard.selectors";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NbRoleHideDirective } from "../../directives/nb-role-hide/nb-role-hide.directive";
import { AsyncPipe, NgIf } from "@angular/common";
import { AssetTreeContextMenuComponent } from "./asset-tree-context-menu/asset-tree-context-menu.component";

function toTreeNode(
  asset: AssetViewModel,
  editMode: boolean,
): TreeNode<AssetViewModel> {
  const isRootAsset = asset.parentAssetId === null;
  return {
    label: asset.name,
    children: asset.children.map((child) => toTreeNode(child, editMode)),
    data: asset,
    expanded: isRootAsset,
    selectable: true,
    icon: asset.children.length === 0 ? "fa-solid fa-diamond fa-xs" : null,
    draggable: editMode && !isRootAsset,
    droppable: editMode,
    key: asset.assetId.toString(),
  };
}

@Component({
  selector: "ngx-asset-tree",
  templateUrl: "./asset-tree.component.html",
  styleUrls: ["./asset-tree.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [TreeDragDropService],
  imports: [
    NbButtonModule,
    NbTooltipModule,
    FontAwesomeModule,
    NbRoleHideDirective,
    NgIf,
    Tree,
    AssetTreeContextMenuComponent,
    AsyncPipe,
  ],
})
export class AssetTreeComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly Permissions = Permissions;
  public readonly destroyed$: Subject<boolean> = new Subject();
  public readonly faCompressArrowsAlt = faCompressArrowsAlt;
  public readonly faExpandArrowsAlt = faExpandArrowsAlt;
  public readonly faEdit = faEdit;
  public readonly faSave = faSave;
  public readonly faTimes = faTimes;

  public editMode$: Subject<boolean> = new BehaviorSubject(false);
  public selectedNode: TreeNode<AssetViewModel>;
  public treeNodes$: Observable<TreeNode<AssetViewModel>[]>;
  private assetTree$: Observable<AssetViewModel[]>;

  constructor(
    private store: Store<AppState>,
    private toastr: NbToastrService,
    private dialogService: NbDialogService,
    private helixAssetTreeClient: HelixAssetTreeClient,
  ) {}

  @Output() assetSelected: EventEmitter<AssetViewModel> = new EventEmitter();

  @ViewChild("assettree", { static: true }) tree: Tree;
  @ViewChild("searchInput", { static: true }) searchInput: ElementRef;

  @Input() useContextMenu: boolean = false;
  @Input() editable: boolean = false;
  @Input() showHiddenAssets: boolean = false;
  @Input() showLoadingSpinner: boolean = false;
  @Input() showTags: boolean = false;

  selectedAssetId$: Observable<number>;

  ngAfterViewInit() {
    combineLatest([this.treeNodes$, this.selectedAssetId$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([treeNodes, selectedAssetId]) => {
        if (!selectedAssetId || !treeNodes || treeNodes.length === 0) {
          return;
        }
        this.setCurrentlySelectedAsset(selectedAssetId, treeNodes);
      });
  }

  ngOnInit() {
    this.assetTree$ = this.store.pipe(
      select((state) => state.dashboard.assetTree),
      map((value) => this.filterOutAllAssetsSetToHide(value)),
      map((assetTree) => assetTree.slice(0)),
    );

    this.treeNodes$ = combineLatest([this.assetTree$, this.editMode$]).pipe(
      map(([assetTree, editMode]) =>
        assetTree.map((asset: AssetViewModel) => toTreeNode(asset, editMode)),
      ),
    );

    this.selectedAssetId$ = this.store.select(selectSelectedAssetId);
  }

  setCurrentlySelectedAsset(
    selectedAssetId: number,
    treeNodes: TreeNode<AssetViewModel>[],
  ) {
    if (treeNodes[0]?.data?.assetId === selectedAssetId) {
      this.selectedNode = treeNodes[0];
      return;
    }

    this.executeRecursively(treeNodes[0], (node) => {
      if (node.data.assetId === selectedAssetId) {
        this.selectedNode = node;
      }
    });
  }

  onNodeSelected(node: TreeNode<AssetViewModel>) {
    this.selectedNode = node;
    this.assetSelected.emit(node.data);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public collapseAll() {
    const root = this.tree.value[0];
    this.executeRecursively(root, (n) => (n.expanded = false));
    root.expanded = true;
  }

  public expandOnce() {
    const root = this.tree.value[0];
    const toExpand = [];
    this.executeRecursively(root, (n) => {
      if (n.expanded) {
        toExpand.push(...n.children);
      }
    });
    toExpand.forEach((n) => (n.expanded = true));
  }

  private executeRecursively(node: TreeNode, fn: (node: TreeNode) => void) {
    if (!node?.children?.length) {
      return;
    }

    for (const child of node.children) {
      this.executeRecursively(child, fn);
    }

    fn(node);
  }

  public save() {
    firstValueFrom(
      this.dialogService.open(DeleteConfirmComponent, {
        context: {
          title: "Save changes to the asset tree?",
          message:
            "This action cannot be undone. Don't come crying if you've butchered it!",
        },
      }).onClose,
    ).then((confirmation) => {
      if (!confirmation) {
        return;
      }

      this.showLoadingSpinner = true;

      firstValueFrom(
        this.helixAssetTreeClient.adjustAssetTree(
          this.getAllAdjustments(this.tree.value[0]),
        ),
      ).then(() => {
        this.toastr.success("Updated asset tree", "Asset tree");
        this.store.dispatch(new GetAssetTree());
        this.showLoadingSpinner = false;
        this.resetAssetTreeSettings();
      });
    });
  }

  public edit() {
    this.editMode$.next(true);
  }

  public cancel() {
    this.resetAssetTreeSettings();
  }

  private resetAssetTreeSettings() {
    this.editMode$.next(false);
  }

  private filterOutAllAssetsSetToHide(assetTrees: AssetViewModel[]) {
    if (this.showHiddenAssets) {
      return assetTrees;
    }
    return assetTrees
      .filter((value) => !value.hideInAssetTree)
      .map((assetTree) => {
        assetTree = Object.assign({}, assetTree);
        if (assetTree.children && assetTree.children.length !== 0) {
          assetTree.children = this.filterOutAllAssetsSetToHide(
            assetTree.children,
          );
        }
        return assetTree;
      });
  }

  private getAllAdjustments(
    node: TreeNode<AssetViewModel>,
  ): AssetTreeAdjustments[] {
    if (!node?.children?.length) {
      return [];
    }

    const adjustments: AssetTreeAdjustments[] = [];

    for (let index = 0; index < node.children.length; index++) {
      const child = node.children[index];
      adjustments.push({
        assetToChangeId: child.data.assetId,
        newParentId: node.data.assetId,
        newIndex: index,
      });
      adjustments.push(...this.getAllAdjustments(child));
    }

    return adjustments;
  }
}
